import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { Input } from '@mobi/component-library/Selection&Input/Inputs'
import { DropdownInput } from '@mobi/component-library/Selection&Input/DropdownInput'
import { DocumentPermission } from '@mobi/account/Areas/SignUp/Components/Verification'
import { consentText } from '@mobi/account/Areas/SignUp/Components/Message'
import { DocumentBodyStyled, FormLayout } from './Layout.styled'
import {
  type TypeSignUpApplicationResponse,
  useCountryList,
  useVerifyPassport,
  type TypePassportDetailsRequest,
  useApplication,
  useApplicationSubmit,
} from '../../Hooks'
import {
  ALPHANUMERIC_REGEX,
  getNameValidationMessage,
  getRequiredValidationMessage,
  isValidName,
  isVerificationSuccessful,
  routes,
} from '@mobi/account/Areas/SignUp/Utils'
import {
  getSignUpState,
  setIsApplicationPendingReview,
  setNumberOfAttemptsToVerify,
  setSignupApplication,
  setSignupVerification,
} from '../../Store'
import { SignupError } from '../SignupError'
import { Contract } from '@mobi/web-native-comms'
import { ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT } from '../../Utils'
import { trackVerificationSuccess } from '@mobi/account/Areas/Verification/Utils/ga'
import { trackSignUpError } from '../../Utils/ga'
import { unwrapErrorMessage } from '@mobi/utils/errors'
import { NameInput } from '@mobi/account/Common'
import {
  PopularCountryList,
  AlphabeticalCountryList,
} from '@mobi/account/Common/CountryOptionGroups/CountryOptionGroups'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { useHostContextFeature } from '@mobi/account/Hooks'

interface PassportProps {
  trackOptimoveEvent: (event: Contract['OPTIMOVE_TRACK']) => void
}

export const Passport = ({ trackOptimoveEvent }: PassportProps) => {
  const [passportNumber, setPassportNumber] = React.useState('')
  const [passportCountry, setPassportCountry] = React.useState<string | null>('AUS')
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)

  const isAmlSignupRiskAssessmentEnabled = useHostContextFeature(
    FeatureFlags.AML_SIGNUP_RISK_ASSESSMENT.key
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const signUpState = useSelector(getSignUpState)
  const application = signUpState.signupApplication
  const verification = signUpState.signupVerification
  const countryList = useCountryList()
  const { verifyPassport, verifyPassportStatus } = useVerifyPassport()
  const { applicationSave, applicationSaveStatus } = useApplication({
    setValidationMessages,
    setShowErrorPopup,
  })
  const { applicationSubmit, applicationSubmitStatus } = useApplicationSubmit(
    application.applicationReferenceNumber
  )

  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const allowToProceed =
    hasValidEntries &&
    givePermission &&
    !!passportNumber &&
    !!passportCountry &&
    isValidName(passportCountry) &&
    isValidName(application.firstName) &&
    isValidName(application.middleName, true) &&
    isValidName(application.surname)
  const isBusy =
    countryList.isLoading ||
    verifyPassportStatus === 'pending' ||
    applicationSaveStatus === 'pending' ||
    applicationSubmitStatus === 'pending'

  const onSubmit = () => {
    if (!allowToProceed) {
      return
    }

    if (!isAmlSignupRiskAssessmentEnabled) {
      const passport: TypePassportDetailsRequest = {
        birthdate: application.birthdate,
        firstName: application.firstName,
        surname: application.surname,
        middleName: application.middleName,
        firstNameAlias: application.firstNameAlias,
        lastNameAlias: application.lastNameAlias,
        email: application.email,
        mobileNumber: application.mobileNumber,
        residentialAddress: application.residentialAddress,
        previousAddress: application.previousAddress,
        enquiryId: application.enquiryId,
        applicationReferenceNumber: application.applicationReferenceNumber,
        passportDetails: {
          number: passportNumber,
          passportCountry: passportCountry,
        },
        marketingOptInIndicator: application.marketingOptInIndicator,
        termsAndConditionsText: consentText,
        termsAndConditionsConsent: givePermission,
      }

      verifyPassport(passport, {
        onSuccess: verification => {
          if (application.applicationReferenceNumber != null) {
            const verificationResponse: TypeSignUpApplicationResponse = {
              applicationReferenceNumber: application.applicationReferenceNumber,
              enquiryId: verification.enquiryId,
              duplicateExists: false,
              isDuplicateClosed: false,
              isVerified: verification.isVerified,
              unVerifiedReason: verification.unVerifiedReason,
              potentialFraudMatch: verification.potentialFraudMatch,
              potentialSanctionedMatch: verification.potentialSanctionedMatch,
            }
            dispatch(setSignupVerification(verificationResponse))
            const isSuccessful = isVerificationSuccessful(verificationResponse)

            if (isSuccessful) {
              trackVerificationSuccess('Verified Identification', 'passport')
              trackOptimoveEvent(ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT)
              navigate(routes.password)
              return
            }

            dispatch(setNumberOfAttemptsToVerify())
            if (
              verificationResponse.duplicateExists ||
              verificationResponse.potentialSanctionedMatch ||
              !verificationResponse.isVerified
            ) {
              navigate(routes.signUpUnsuccessful)
              return
            }
          }
        },
        onError: error => {
          trackSignUpError(location.pathname, unwrapErrorMessage(error))
          setShowErrorPopup(true)
        },
      })
    }

    if (isAmlSignupRiskAssessmentEnabled) {
      applicationSave(
        { passportDetails: { number: passportNumber, passportCountry } },
        {
          onSuccess: res => {
            if (res.duplicateExists) {
              return
            }

            applicationSubmit(
              { termsAndConditionsText: consentText, termsAndConditionsConsent: givePermission },
              {
                onSuccess: res => {
                  dispatch(setSignupApplication({ ...application, enquiryId: res.enquiryId }))
                  dispatch(setIsApplicationPendingReview(res.pendingReview))
                  dispatch(
                    setSignupVerification({
                      ...verification,
                      isVerified: res.isVerified,
                      unVerifiedReason: res.unverifiedReason ?? '',
                    })
                  )
                  if (res.isVerified && !res.pendingReview) {
                    trackVerificationSuccess('Verified Identification', 'passport')
                    trackOptimoveEvent(ACCOUNT_VERIFICATION_STATUS_OPTIMOVE_EVENT)
                    navigate(routes.password)
                    return
                  }
                  navigate(routes.signUpUnsuccessful)
                },
                onError: error => {
                  trackSignUpError(location.pathname, unwrapErrorMessage(error))
                  setShowErrorPopup(true)
                },
              }
            )
          },
        }
      )
    }
  }

  if (!(application.applicationReferenceNumber > 0)) {
    return null
  }

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  return (
    <>
      <FormLayout>
        <AccountHeader
          alignItems='start'
          title='Enter your Passport details'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your details as they appear on your ID document.
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <DocumentBodyStyled>
          <NameInput
            firstName={application.firstName}
            middleName={application.middleName}
            lastName={application.surname}
            setFirstName={firstName =>
              dispatch(setSignupApplication({ ...application, firstName }))
            }
            setMiddleName={middleName =>
              dispatch(setSignupApplication({ ...application, middleName }))
            }
            setLastName={surname => dispatch(setSignupApplication({ ...application, surname }))}
            isDisabled={isBusy}
          />
          <Input
            name='passportNumber'
            label='Passport number'
            maxLength={15}
            placeholder='Enter passport number'
            isDisabled={isBusy}
            value={passportNumber}
            onChange={e => {
              const filteredText = e.target.value?.replace(ALPHANUMERIC_REGEX, '')
              setPassportNumber(filteredText)
            }}
            onFocus={() => resetFieldValidation('passportNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                passportNumber: getRequiredValidationMessage(passportNumber),
              }))
            }}
            isInvalid={!!validationMessages.passportNumber}
            helpText={validationMessages.passportNumber}
          />
          <DropdownInput
            name='passportCountry'
            label='Issuing country'
            placeholder='Select country'
            value={passportCountry || ''}
            isDisabled={isBusy}
            onChange={event => {
              setPassportCountry(event.target.value || '')
            }}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                passportCountry: getNameValidationMessage(passportCountry),
              }))
            }}
            isInvalid={!!validationMessages.passportCountry}
            helpText={validationMessages.passportCountry}
          >
            <PopularCountryList countries={countryList.data ?? []} />
            <AlphabeticalCountryList countries={countryList.data ?? []} />
          </DropdownInput>
          <DocumentPermission isBusy={isBusy} onChange={e => setGivePermission(e.target.checked)} />
        </DocumentBodyStyled>

        <footer>
          <Button isFullWidth disabled={!allowToProceed} isLoading={isBusy} onClick={onSubmit}>
            Continue
          </Button>
        </footer>
      </FormLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
